// src/pages/processifyd/ProcessifydHelpTexts.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faList,
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydHelpTexts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [helpTexts, setHelpTexts] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingHelpText, setEditingHelpText] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const [loading, setLoading] = useState(false);

  const initialForm = {
    helpTextName: "",
    helpText: "",
    screenID: "",
    screenSectionID: "",
    client: "",
    active: "Yes",
  };

  const [formData, setFormData] = useState(initialForm);

  // Sample Demo Data
  const demoHelpTexts = [
    {
      id: 1,
      helpTextName: "Command Master Added Successfully",
      helpText: "Command Master Added Successfully!!",
      screenID: "PFYD_ADD_CMDMAST",
      screenSectionID: "",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
    {
      id: 2,
      helpTextName: "Client Added Successfully",
      helpText: "New client has been added to the system successfully.",
      screenID: "PFYD_ADD_CLIENT",
      screenSectionID: "ClientBasicInfo",
      client: "21st Century Financial Services",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "15/01/24 10:20",
      lastUpdatedBy: "Priya Sharma",
      lastUpdateDate: "20/02/24 09:15",
    },
  ];

  const loadSample = () => {
    setHelpTexts(demoHelpTexts);
  };

  const handleAddNew = () => {
    setEditingHelpText(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (ht) => {
    setEditingHelpText(ht);
    setFormData({
      helpTextName: ht.helpTextName,
      helpText: ht.helpText || "",
      screenID: ht.screenID || "",
      screenSectionID: ht.screenSectionID || "",
      client: ht.client || "",
      active: ht.active || "Yes",
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this help text? This action cannot be undone.")) return;
    setHelpTexts((prev) => prev.filter((h) => h.id !== id));
  };

  const handleOpenHelpText = (ht) => {
    // Update this route as per your actual help text detail page
    navigate(`/processifyd-helptexthub/${ht.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.helpTextName.trim() || !formData.helpText.trim()) {
      alert("Help Text Name and Help Text are required.");
      return;
    }

    const newHelpText = {
      id: editingHelpText?.id || Date.now(),
      ...formData,
      createdBy: editingHelpText?.createdBy || "Sanjeev Kumar",
      createdDate: editingHelpText?.createdDate || new Date().toLocaleString("en-IN"),
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: new Date().toLocaleString("en-IN"),
    };

    if (editingHelpText) {
      setHelpTexts((prev) =>
        prev.map((h) => (h.id === editingHelpText.id ? newHelpText : h))
      );
    } else {
      setHelpTexts((prev) => [...prev, newHelpText]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // Filter + Pagination
  const filtered = helpTexts.filter(
    (h) =>
      h.helpTextName.toLowerCase().includes(search.toLowerCase()) ||
      h.helpText.toLowerCase().includes(search.toLowerCase()) ||
      h.screenID.toLowerCase().includes(search.toLowerCase()) ||
      h.client.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const [showList, setShowList] = useState(false);
      
  const handleShowMyHelpText = async () => {
    try {
      setLoading(true);

      // 🔄 Replace this later with API call
      // const response = await axios.get("/api/helptext");
      // setHelpText(response.data);

      // ✅ Temporary Demo Data (remove later)
      setHelpTexts([]);

      setShowList(false);
    } catch (error) {
      console.error("Error fetching help text:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            ❔ Processifyd
          </div>
          <div className="text-xs text-gray-400">
            Help Texts Master Management
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

        <button
          className="md:hidden text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          {isLoggedIn && (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="w-12 px-2 py-1 bg-red-700 rounded-md">⏻</button>
            </div>
          )}
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex flex-row gap-3">
            <button
              onClick={loadSample}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-40 lg:w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              Load Sample Help Texts
            </button>
            <button
              onClick={handleShowMyHelpText}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-32 lg:w-40 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faList} />
              My Help Texts
            </button>            
            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm w-32 lg:w-40 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Help Text
            </button>
          </div>

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by help text name, screen ID or client..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500/60"
            />
          </div>
        </div>

        {/* Help Text Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((ht) => (
            <div
              key={ht.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300 transition-colors line-clamp-2">
                    {ht.helpTextName}
                  </h3>
                  <div className="text-sm text-gray-400 mt-1">
                    Screen: {ht.screenID || "—"}
                  </div>
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-medium ${ht.active === "Yes" ? "bg-emerald-600" : "bg-red-600"}`}>
                  {ht.active === "Yes" ? "Active" : "Inactive"}
                </span>
              </div>

              <p className="mt-4 text-sm text-gray-300 line-clamp-4">
                {ht.helpText || "No help text available"}
              </p>

              <div className="mt-4 text-xs text-gray-500">
                <strong>Client:</strong> {ht.client}
              </div>

              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenHelpText(ht)}
                  className="flex-1 text-xs lg:text-sm w-20 lg:w-24 px-2 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  Open
                </button>

                <button
                  onClick={() => handleEdit(ht)}
                  className="w-12 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit Help Text"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>

                <button
                  onClick={() => handleDelete(ht.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete Help Text"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search ? "No help texts match your search" : "No help texts yet. Click 'New Help Text' or 'Load Sample Help Texts'"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {showList && (
          <div className="mt-4 bg-black text-white rounded-lg p-4">
            {loading ? (
              <p className="text-yellow-200">Loading help texts...</p>
            ) : helpTexts.length === 0 ? (
              <p className="text-yellow-200">No help text found.</p>
            ) : (
              <ul className="space-y-2">
                {helpTexts.map((ht) => (
                  <li
                    key={ht.id}
                    className="border border-gray-700 rounded p-2"
                  >
                    <p className="font-semibold text-gray-100">{ht.helpTextName}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Add / Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingHelpText ? "Edit Help Text" : "New Help Text"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white w-12 text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm mb-1.5">Help Text Name <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.helpTextName}
                    onChange={(e) => setFormData({ ...formData, helpTextName: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. Command Master Added Successfully"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Help Text <span className="text-red-400">*</span></label>
                  <textarea
                    required
                    value={formData.helpText}
                    onChange={(e) => setFormData({ ...formData, helpText: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="Enter the help text message..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Screen ID</label>
                    <input
                      value={formData.screenID}
                      onChange={(e) => setFormData({ ...formData, screenID: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. PFYD_ADD_CMDMAST"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Screen Section ID</label>
                    <input
                      value={formData.screenSectionID}
                      onChange={(e) => setFormData({ ...formData, screenSectionID: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. BasicInfo or empty"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Client <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. 21st Century Financial Services"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">Active <span className="text-red-400">*</span></label>
                  <select
                    value={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                    className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingHelpText ? "Update Help Text" : "Create Help Text"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydHelpTexts;